import { Formations } from "@/api/privateRequests/squad-builder/get-formations";
import { convertSBCPrice } from "@/functions/convertPlayerPrice";
import {
  SelectorFilterType,
  UpdateFilterType,
} from "@/pages/Players/FiltersPanel/filterTypes";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import { usedFeature } from "@/redux/user/userSlice";
import {
  PayloadAction,
  Selector,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { evolutionPathPair } from "@repo/shared/evolutions";
import { fetchBaseData } from "@repo/shared/redux";
import { MetaRating, getCoinValue } from "@repo/shared/types";
import { captureMessage } from "@sentry/react";
import { isMobile } from "react-device-detect";

import { APIStatus } from "../../../api/APIStatus.type";
import { importSquad as _importSquad, getPlayers } from "../../../api/api";
import {
  BestFormationFromBackendSquad,
  buildSquads as _buildSquads,
  buildSquadsWithBestFormation as _buildSquadsWithBestFormation,
} from "../../../api/privateRequests/squad-builder/build-squad";
import {
  SavedOrSharedSquadResponseData,
  getSavedSquad,
} from "../../../api/privateRequests/squad-builder/get-saved-squad";
import { getSharedSquad } from "../../../api/privateRequests/squad-builder/get-shared-squad";
import {
  SavedSquad,
  getSquads,
} from "../../../api/privateRequests/squad-builder/get-squads";
import {
  SaveSquadRes,
  saveSquad as _saveSquad,
} from "../../../api/privateRequests/squad-builder/save-squad";
import { recommendPlayers } from "../../../api/publicRequests/squad-builder/recommendPlayers";
import { getCodeFromTactic as _getCodeFromTactic } from "../../../api/publicRequests/tactics/getCodeFromTactic";
import { getTacticFromCode as _getTacticFromCode } from "../../../api/publicRequests/tactics/getTacticFromCode";
import {
  ActiveFilter,
  FilterKey,
} from "../../../components/features/SquadBuilder/components/FiltersBar/FiltersBar";
import { ChemistryProfiles } from "../../../functions/calculateChemistry";
import { PlayerRole } from "../../players/playersSlice";
import { AppDispatch, IRootState } from "../../store";
import { Tactic } from "../../tactics/tacticsSlice";
import { defensiveApproachForLineHeight } from "../defensiveApproachForLineHeight";
import { findMetaRating } from "../findMetaRating";
import {
  LeftPanelMode,
  PlayerFilter,
  RatingFilter,
  RightPanelMode,
  Scope,
  SquadBuilderPlayerType,
  buildClubLinks,
  calculateMetaRatingAndChemistry,
  defaultTactic,
  emptyPlayer,
  numberToMetal,
  prepareFiltesForAPI,
  roleWeightsByRoleId,
  setEmptyAttributes,
  setEmptyBench,
  setEmptyPlayerFilters,
  setEmptySquad,
} from "../squadBuilderUtils";
import { Squad } from "../squadBuilderUtils";

// Returns the first argument that is neither undefined nor null, or undefined if none exist.
const pickDefined = <T>(...values: Array<T | undefined | null>) =>
  values.find((value) => value !== undefined && value !== null);

type DefaultSquadBuilderState = {
  squad: Squad;
  tempSquad: Squad;
  bestFormationIncludedPlayers: (SearchedPlayer | SquadBuilderPlayerType)[];
  benchItems: SquadBuilderPlayerType[];
  tempBenchItems: SquadBuilderPlayerType[];
  builtSquads: Squad[];
  statusBuildSquad: APIStatus;
  statusBuildSquadWithBestFormation: APIStatus;
  statusGetRecommendedPlayers: APIStatus;
  statusImportSquad: APIStatus;
  statusSaveSquad: APIStatus;
  statusFetchSavedSquads: APIStatus;
  statusLoadSpecficSquad: APIStatus;
  statusInsertTacticCode: APIStatus;
  statusUpdateTacticCode: APIStatus;
  selectedCountries: number[];
  selectedLeagues: number[];
  selectedClubs: number[];
  selectedPnPs: number[];
  selectedVersions: number[];
  selectedMetals: number[];
  budget: number;
  scope: Scope;
  changingPitchItemOnIndex: number;
  recommendedPlayers: SquadBuilderPlayerType[];
  rightPanelMode: RightPanelMode;
  leftPanelMode: LeftPanelMode;
  playerFilters: PlayerFilter[];
  chosenTactic: Tactic;
  tempChosenTactic: Tactic;
  tempFormation?: string;
  chemistryProfiles: ChemistryProfiles;
  clubLinks: Record<number, number>;
  savedSquads: SavedSquad[];
  loadedSaveSquadIndex: number;
  excludeIconsHeroes: boolean;
  ratingFilter: RatingFilter;
  playerRoles: PlayerRole[];
  formations: Formations;
  includeLoanPlayers: boolean;
  selectedLiveEventId?: number;
  isBestFormationOn: boolean;
  hasChangedTactic: boolean;
};
const initialState: DefaultSquadBuilderState = {
  squad: {
    ...setEmptySquad(),
    players: setEmptySquad().players.map((p, index) => ({
      ...p,
      playerRoleId: defaultTactic.playerRoles[index].playerRoleId,
    })),
  },

  tempSquad: setEmptySquad(),
  benchItems: [],
  tempBenchItems: [],
  builtSquads: [],
  bestFormationIncludedPlayers: [],
  statusBuildSquad: "idle",
  statusBuildSquadWithBestFormation: "idle",
  statusGetRecommendedPlayers: "idle",
  statusImportSquad: "idle",
  statusSaveSquad: "idle",
  statusFetchSavedSquads: "idle",
  statusLoadSpecficSquad: "idle",
  statusInsertTacticCode: "idle",
  statusUpdateTacticCode: "idle",
  selectedCountries: [],
  selectedLeagues: [],
  selectedClubs: [],
  selectedPnPs: [],
  selectedMetals: [],
  selectedVersions: [],
  budget: Number.MAX_VALUE,
  scope: "market",
  changingPitchItemOnIndex: -1,
  recommendedPlayers: [],
  rightPanelMode: "default",
  leftPanelMode: "chem-overview",
  playerFilters: setEmptyPlayerFilters({
    playStylesFilterOptions: [],
    playStylesPlusFilterOptions: [],
  }),
  chosenTactic: defaultTactic,
  tempChosenTactic: undefined,
  chemistryProfiles: {} as ChemistryProfiles,
  clubLinks: {},
  savedSquads: [],
  loadedSaveSquadIndex: undefined,
  excludeIconsHeroes: false,
  ratingFilter: {},
  playerRoles: [],
  formations: {} as Formations,
  includeLoanPlayers: false,
  isBestFormationOn: false,
  hasChangedTactic: false,
};

export const buildSquadsWithBestFormation = createAsyncThunk<
  { squad: Squad }[]
>("build-squads-with-best-formation", async (_, { getState, dispatch }) => {
  const state = getState() as IRootState;
  const {
    scope,
    budget,
    selectedCountries,
    selectedLeagues,
    selectedClubs,
    selectedPnPs,
    selectedVersions,
    selectedMetals,
    squad,
    excludeIconsHeroes,
    ratingFilter,
    includeLoanPlayers,
    selectedLiveEventId,
    bestFormationIncludedPlayers,
    benchItems,
  } = state.defaultSquadBuilderReducer;
  const res = await _buildSquadsWithBestFormation({
    playersFrom: scope,
    players: bestFormationIncludedPlayers.map((player) => ({
      resourceId: player.resourceId,
      eaPlayerId: player.eaPlayerId,
    })),
    budget,
    benchBudget: budget * 0.1,
    manager: {
      country: squad.manager?.country || null,
      league: squad.manager?.league || null,
    },
    country: selectedCountries,
    league: selectedLeagues,
    club: selectedClubs,
    pastAndPresentClub: selectedPnPs,
    version: selectedVersions,
    metal: selectedMetals.map((metalId) => numberToMetal(metalId)),
    excludeIconsHeroes,
    minRating: ratingFilter.max,
    maxRating: ratingFilter.max,
    includeLoanPlayers,
    liveEventId: selectedLiveEventId,
  });

  if (res.data.length === 0) {
    throw new Error("No squads found");
  }

  return res.data.map((ss) => ({
    squad: withBenchRoles(
      ss,
      state.defaultSquadBuilderReducer.playerRoles,
      benchItems,
    ),
  }));
});

/*
  A Best formation squad as the backend sends it, made into the squad
  builder's own: the tactic's roles run on past the eleven to the bench (the
  squad's own) and the reserves (the ones already set).
*/
const withBenchRoles = (
  ss: BestFormationFromBackendSquad,
  playerRoles: DefaultSquadBuilderState["playerRoles"],
  benchItems: DefaultSquadBuilderState["benchItems"],
) => {
  const roleOf = (playerRoleId: number) => ({
    playerRoleId,
    focus: getDefaultFocus(playerRoles, playerRoleId),
  });
  return {
    ...ss,
    suggestedSquadChemistry: ss.chemistry,
    tactic: {
      ...ss.tactic,
      playerRoles: [
        ...ss.tactic.playerRoles,
        ...ss.players.slice(11).map((p) => roleOf(p.playerRoleId)),
        ...benchItems.slice(7).map((p) => roleOf(p.playerRoleId)),
      ],
    },
  } as unknown as Squad;
};

/**
 * Puts a Best formation squad built elsewhere (My Club's onboarding) on the
 * pitch, as if the squad builder had built it: Best formation on, the squad
 * as its one suggestion, and picked.
 */
export const loadBestFormationSquad = createAsyncThunk<
  void,
  BestFormationFromBackendSquad
>("load-best-formation-squad", async (ss, { getState, dispatch }) => {
  const { playerRoles, benchItems } = (getState() as IRootState)
    .defaultSquadBuilderReducer;
  dispatch(updateBestFormation(true));
  dispatch(
    buildSquadsWithBestFormation.fulfilled(
      [{ squad: withBenchRoles(ss, playerRoles, benchItems) }],
      "load-best-formation-squad",
    ),
  );
  dispatch(addSquad({ index: 0 }));
});

export const buildSquadsWithClubFromBanner = createAsyncThunk<void>(
  "build-squads-with-club-from-banner",
  async (_, { dispatch }) => {
    dispatch(updateScope("club"));
    dispatch(buildSquads());
  },
);

export const buildSquads = createAsyncThunk<Squad[]>(
  "build-squads",
  async (_, { getState, dispatch }) => {
    const state = getState() as IRootState;

    const {
      scope,
      budget,
      selectedCountries,
      selectedLeagues,
      selectedClubs,
      selectedPnPs,
      selectedVersions,
      selectedMetals,
      squad,
      chosenTactic,
      playerFilters,
      excludeIconsHeroes,
      ratingFilter,
      formations,
      benchItems,
      includeLoanPlayers,
      selectedLiveEventId,
    } = state.defaultSquadBuilderReducer;

    const inputPlayersToInclude: {
      playerRoleId: number;
      position: string;
      resourceId?: number;
      eaPlayerId?: number;
    }[] = [];

    const pitchIsFull = squad.players.every((p) => p.resourceId);
    for (let i = 0; i < 11; i++) {
      inputPlayersToInclude.push({
        playerRoleId: squad.players[i].playerRoleId,
        resourceId: pitchIsFull ? undefined : squad.players[i].resourceId,
        eaPlayerId: pitchIsFull ? undefined : squad.players[i].eaPlayerId,
        position:
          formations[chosenTactic.teamTactics.formationId]?.positions[i],
      });
    }
    // For bench players
    for (let i = 0; i < 7; i++) {
      inputPlayersToInclude.push({
        playerRoleId: benchItems[i].playerRoleId,
        resourceId: pitchIsFull ? undefined : benchItems[i].resourceId,
        eaPlayerId: pitchIsFull ? undefined : benchItems[i].eaPlayerId,
        position:
          benchItems[i]?.position ||
          formations[chosenTactic.teamTactics.formationId]?.positions[i + 11],
      });
    }

    const filters = prepareFiltesForAPI(playerFilters);
    const builtSquads = await _buildSquads({
      playersFrom: scope,
      players: inputPlayersToInclude,
      budget,
      benchBudget: budget * 0.1,
      manager: {
        country: squad.manager?.country || null,
        league: squad.manager?.league || null,
      },
      country: selectedCountries,
      league: selectedLeagues,
      club: selectedClubs,
      pastAndPresentClub: selectedPnPs,
      version: selectedVersions,
      metal: selectedMetals.map((metalId) => numberToMetal(metalId)),
      playerFilters: filters,
      formationId: chosenTactic.teamTactics.formationId,
      excludeIconsHeroes,
      minRating: ratingFilter.min,
      maxRating: ratingFilter.max,
      includeLoanPlayers,
      liveEventId: selectedLiveEventId,
    });

    const builtSquadsWithChemistry = builtSquads.data.map((ss) => {
      return {
        ...ss,
        suggestedSquadChemistry: ss.chemistry,
        chemistry: {},
      };
    }) as Squad[];

    return builtSquadsWithChemistry;
  },
);

const getRatingMinMax = (selectedMetals: number[]) => {
  let ratingMin: number | null = null;
  let ratingMax: number | null = null;

  if (selectedMetals.length > 0) {
    // Bronze (2)
    if (selectedMetals.includes(2)) {
      ratingMax = 64;
    }

    // Silver (1)
    if (selectedMetals.includes(1)) {
      if (ratingMax === null) {
        ratingMax = 74;
      }
      if (!selectedMetals.includes(2)) {
        ratingMin = 65;
      }
    }

    // Gold (0)
    if (selectedMetals.includes(0)) {
      ratingMin =
        selectedMetals.includes(1) || selectedMetals.includes(2) ? null : 75;
      ratingMax = 100;
    }
  }
  return { ratingMin, ratingMax };
};

export const fetchRecommendedPlayers = createAsyncThunk(
  "fetch-player-recs",
  async (_, { getState }) => {
    const state = getState() as IRootState;
    const {
      budget,
      squad,
      scope,
      changingPitchItemOnIndex,
      selectedClubs,
      selectedPnPs,
      selectedCountries,
      selectedLeagues,
      selectedVersions,
      selectedMetals,
      chosenTactic,
      playerFilters,
      excludeIconsHeroes,
      benchItems,
      formations,
      includeLoanPlayers,
    } = state.defaultSquadBuilderReducer;

    const { ratingMin, ratingMax } = getRatingMinMax(selectedMetals);

    // If bench player use getPlayers instead of recommendPlayers

    if (changingPitchItemOnIndex > 10) {
      let queryParams: any[] = [];
      if (selectedCountries.length > 0) {
        queryParams.push({ country: selectedCountries });
      }
      if (budget > 0 && budget < Number.MAX_VALUE) {
        queryParams.push({ priceMax: [budget] }, { priceMin: [1] });
      }
      if (selectedLeagues.length > 0) {
        queryParams.push({ league: selectedLeagues });
      }
      if (selectedClubs.length > 0) {
        queryParams.push({ club: selectedClubs });
      }
      if (selectedPnPs.length > 0) {
        queryParams.push({ "past-and-present-club": selectedPnPs });
      }
      if (selectedVersions.length > 0) {
        queryParams.push({ rarity: selectedVersions });
      }
      if (benchItems[changingPitchItemOnIndex - 11]?.position) {
        queryParams.push({
          position: [benchItems[changingPitchItemOnIndex - 11].position],
        });
      }
      if (ratingMin !== null) {
        queryParams.push({ ratingMin: [ratingMin] });
      }
      if (ratingMax !== null) {
        queryParams.push({ ratingMax: [ratingMax] });
      }
      if (excludeIconsHeroes) {
        queryParams.push({ "exclude-icons-heroes": [true] });
      }
      if (includeLoanPlayers) {
        queryParams.push({ "include-loan-players": [true] });
      }

      const res = await getPlayers({
        page: 1,
        sort: "meta-rating",
        myClub: scope === "club",
        // myEvolvedPlayers: true,
        playerRoleId: benchItems[changingPitchItemOnIndex - 11]?.playerRoleId,
        queryParams,
      });

      const convertedRes = res.players.map((player) => {
        const metaRatings = [
          {
            metaRating: player.metaRating,
            playerRoleId: player.playerRoleId,
            chemistry: 0,
            chemstyleId: 0,
          },
        ];
        return {
          ...player,
          possiblePositions: player.positions,
          metaRatings: metaRatings,
          playerRoleId: player.playerRoleId,
          position: benchItems[changingPitchItemOnIndex - 11]?.position,
        };
      }) as SquadBuilderPlayerType[];
      return convertedRes;
    }

    const playersReq = [];
    for (let i = 0; i < 11; i++) {
      playersReq.push({
        playerRoleId: squad.players[i].playerRoleId,
        resourceId: squad.players[i].resourceId,
        eaPlayerId: squad.players[i].eaPlayerId,
        position:
          formations[chosenTactic.teamTactics.formationId]?.positions[i],
      });
    }

    const filters = prepareFiltesForAPI(playerFilters);

    const res = await recommendPlayers({
      playerIndex: changingPitchItemOnIndex,
      budget,
      formationId: chosenTactic.teamTactics.formationId,
      players: playersReq,
      playersFrom: scope,
      manager: {
        country: squad.manager?.country || null,
        league: squad.manager?.league || null,
      },
      version: selectedVersions,
      metal: selectedMetals.map((metalId) => numberToMetal(metalId)),
      country: selectedCountries,
      league: selectedLeagues,
      club: selectedClubs,
      pastAndPresentClub: selectedPnPs,
      playerFilters: filters,
      excludeIconsHeroes: excludeIconsHeroes,
      includeLoanPlayers: includeLoanPlayers,
    });

    return res;
  },
);

export const getTacticFromCode = createAsyncThunk<
  Tactic,
  {
    code: string;
  }
>("get-tactic-from-code", async ({ code }) => {
  const res = await _getTacticFromCode({ code });
  return res;
});

export const getCodeFromTactic = createAsyncThunk<string, { tactic: Tactic }>(
  "get-code-from-tactic",
  async ({ tactic }) => {
    const res = await _getCodeFromTactic({ tactic });
    return res.eaCode;
  },
);

export const movePlayer = createAsyncThunk<
  void,
  {
    fromIndex: number;
    toIndex: number;
  }
>("move-player", async ({ fromIndex, toIndex }, { getState, dispatch }) => {
  dispatch(internalMovePlayer({ from: fromIndex, to: toIndex }));
  dispatch(updateChangingPitchItemOnIndex(-1));
});

export const importSquad = createAsyncThunk("import-squad", async () =>
  _importSquad(),
);

export const saveSquad = createAsyncThunk<
  SaveSquadRes,
  {
    squadName?: string;
  }
>("save-squad", async ({ squadName: squadNameParam }, { getState }) => {
  const state = getState() as IRootState;
  const {
    squad,
    chosenTactic,
    savedSquads,
    loadedSaveSquadIndex,
    benchItems,
    hasChangedTactic,
  } = state.defaultSquadBuilderReducer;
  const overwritingSquad = savedSquads[loadedSaveSquadIndex]?.id;
  const squadName = squadNameParam || savedSquads[loadedSaveSquadIndex]?.name;

  let saveSquadData = {
    name: squadName,
    formation: chosenTactic.teamTactics.formationId,
    players: [
      ...squad.players.map((p) => ({
        playerRoleId: p.playerRoleId,
        resourceId: p.resourceId,
        ...evolutionPathPair(p.evolutionPath, p.pathwayPath),
        evoPathId: p.evoPathId,
      })),
      ...benchItems.map((p) => ({
        playerRoleId: p.playerRoleId,
        resourceId: p.resourceId,
        ...evolutionPathPair(p.evolutionPath, p.pathwayPath),
        evoPathId: p.evoPathId,
      })),
    ],
    manager: {
      countryId: squad.manager?.country,
      leagueId: squad.manager?.league,
    },
  };

  if (hasChangedTactic || !overwritingSquad) {
    saveSquadData["tactic"] = {
      ...chosenTactic,
      playerRoles: chosenTactic.playerRoles.map((p) => ({
        playerRoleId: p.playerRoleId,
        focus: p.focus,
      })),
    };
  }
  if (overwritingSquad) {
    saveSquadData["squadId"] = savedSquads[loadedSaveSquadIndex]?.id;
  }

  const res = await _saveSquad(saveSquadData);
  return res;
});

export const fetchSquads = createAsyncThunk<SavedSquad[]>(
  "fetch-squads",
  async () => getSquads(),
);

export const fetchSpecificSquad = createAsyncThunk<
  {
    squad: SavedOrSharedSquadResponseData;
    index: number;
    sharedOrSaved: "shared" | "saved";
  },
  {
    index?: number;
    squadId?: string;
    sharedOrSaved: "shared" | "saved";
  }
>(
  "fetch-specific-squad",
  async ({ index, squadId, sharedOrSaved }, { getState }) => {
    const state = getState() as IRootState;

    let squad: SavedOrSharedSquadResponseData;
    if (sharedOrSaved === "shared") {
      squad = await getSharedSquad(squadId);
    } else {
      squad = await getSavedSquad(
        squadId ?? state.defaultSquadBuilderReducer.savedSquads[index]?.id,
      );
    }
    return { squad, index, sharedOrSaved };
  },
);

/* Live events whose squad must use one formation. Stopgap until the live
   event carries its formation from the backend. */
const LIVE_EVENT_FORMATIONS: Record<number, string> = { 10: "f442" };

export const lockedFormationForLiveEvent = (liveEventId?: number) =>
  liveEventId === undefined ? undefined : LIVE_EVENT_FORMATIONS[liveEventId];

const getDefaultFocus = (playerRoles: PlayerRole[], playerRoleId: number) => {
  const playerRole = playerRoles.find((pr) => pr.id === playerRoleId);
  return playerRole?.focuses[0]?.name;
};

/**
 * Resolves a pitch-or-bench index to the item it addresses.
 *
 * `changingPitchItemOnIndex` spans both surfaces: 0-10 are the pitch slots in
 * `squad.players`, 11+ are bench slots in `benchItems` at `index - 11`.
 * Returns undefined when the slot does not exist, so callers can bail instead
 * of writing through a missing entry.
 */
const squadOrBenchItem = (
  state: DefaultSquadBuilderState,
  index: number,
): SquadBuilderPlayerType | undefined =>
  index > 10 ? state.benchItems[index - 11] : state.squad.players[index];

export const getPlayStyleFilterOptions = createAsyncThunk(
  "get-play-style-filter-options",
  async (_, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const playStyleFilter = state.playersReducer.playerFilters.find(
      (f) => f.key === "playStyles",
    );
    const playStylePlusFilter = state.playersReducer.playerFilters.find(
      (f) => f.key === "playStylesPlus",
    );

    dispatch(
      loadPlayStyleFilterOptions({
        playStyleFilter: playStyleFilter as SelectorFilterType,
        playStylePlusFilter: playStylePlusFilter as SelectorFilterType,
      }),
    );
    return;
  },
);

/** `name` and `description` are the caller's translated `tactic.custom*` strings. */
export const setCustomTactic = (
  tactic: Tactic,
  { name, description }: { name: string; description: string },
) => ({
  ...tactic,
  name,
  tags: [],
  author: "",
  popularityPercentage: 0,
  socials: {},
  description,
  logoUrl: `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/tactics/logos/preset2.png`,
});

const extractPlayStylesFilterOptions = (playerFilters: PlayerFilter[]) => {
  // The options are the same for all players, so the index doesn't matter
  return {
    playStylesFilterOptions: (
      playerFilters[0].find((f) => f.key === "playStyles") as SelectorFilterType
    )?.options,
    playStylesPlusFilterOptions: (
      playerFilters[0].find(
        (f) => f.key === "playStylesPlus",
      ) as SelectorFilterType
    )?.options,
  };
};

const defaultSquadBuilderSlice = createSlice({
  name: "defaultSquadBuilder",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchRecommendedPlayers.pending, (state) => {
      state.statusGetRecommendedPlayers = "pending";
    });
    builder.addCase(fetchRecommendedPlayers.fulfilled, (state, action) => {
      state.statusGetRecommendedPlayers = "fulfilled";
      state.recommendedPlayers = action.payload;
    });
    builder.addCase(fetchBaseData.fulfilled, (state, action) => {
      state.clubLinks = buildClubLinks(action.payload.managerData.clubs);
      state.playerRoles = action.payload.playerRoles;
      state.chemistryProfiles = action.payload.chemistryProfiles;
      state.formations = Object.fromEntries(
        action.payload.formations.map(({ id, ...formation }) => [
          id,
          formation,
        ]),
      );
      const formation =
        state.formations[state.chosenTactic.teamTactics.formationId];
      const benchItems = [];
      for (let i = 0; i < 12; i++) {
        benchItems.push({
          ...emptyPlayer,
          position: formation.positions[i + 11],
          playerRoleId: formation?.playerRoleIds[i + 11],
        });
      }
      state.benchItems = benchItems;
    });
    builder.addCase(fetchRecommendedPlayers.rejected, (state) => {
      state.statusGetRecommendedPlayers = "rejected";
    });
    builder.addCase(buildSquads.pending, (state) => {
      state.statusBuildSquad = "pending";
    });
    builder.addCase(buildSquads.fulfilled, (state, action) => {
      state.statusBuildSquad = "fulfilled";
      const evoPlayers = state.squad.players.filter(
        (p) => p.evoPathId || p.evolutionPath,
      );

      state.builtSquads = [
        ...action.payload.map((s) => ({
          ...s,
          players: s.players.map((p) => {
            const evoPlayer = evoPlayers.find(
              (ep) => ep.resourceId === p.resourceId,
            );
            return { ...p, ...evoPlayer };
          }),
        })),
      ];

      if (!isMobile) {
        state.rightPanelMode = "suggested-squads";
      }
    });

    builder.addCase(buildSquads.rejected, (state) => {
      state.statusBuildSquad = "rejected";
    });
    builder.addCase(buildSquadsWithBestFormation.pending, (state) => {
      state.statusBuildSquad = "pending";
      state.statusBuildSquadWithBestFormation = "pending";
    });
    builder.addCase(buildSquadsWithBestFormation.rejected, (state) => {
      state.statusBuildSquad = "rejected";
      state.statusBuildSquadWithBestFormation = "rejected";
    });
    builder.addCase(buildSquadsWithBestFormation.fulfilled, (state, action) => {
      state.statusBuildSquad = "fulfilled";
      state.statusBuildSquadWithBestFormation = "fulfilled";
      const evoPlayers = state.squad.players.filter(
        (p) => p.evoPathId || p.evolutionPath,
      );
      state.builtSquads = [
        ...action.payload.map((s) => ({
          ...s.squad,
          players: s.squad.players.map((p) => {
            const evoPlayer = evoPlayers.find(
              (ep) => ep.resourceId === p.resourceId,
            );
            return { ...p, ...evoPlayer };
          }),
        })),
      ];
      if (!isMobile) {
        state.rightPanelMode = "suggested-squads";
      }
    });
    builder
      .addCase(importSquad.fulfilled, (state, action) => {
        const { manager, players, tactic } = action.payload;

        state.statusImportSquad = "fulfilled";
        state.squad.manager = {
          league: manager.league,
          country: manager.country,
        };
        const playerRoles = tactic?.playerRoles;
        let tacticPlayerRoles = [];
        if (tactic) {
          if (playerRoles.length < 23) {
            tacticPlayerRoles = playerRoles.map((p) => ({
              playerRoleId: p.playerRoleId,
              focus:
                p.focus || getDefaultFocus(state.playerRoles, p.playerRoleId),
            }));

            for (let i = 11; i < 23; i++) {
              let playerRoleId = {};
              if (players[i]?.resourceId) {
                playerRoleId = {
                  playerRoleId: players[i]?.metaRatings?.[0]?.playerRoleId,
                  focus: getDefaultFocus(
                    state.playerRoles,
                    players[i]?.metaRatings?.[0]?.playerRoleId,
                  ),
                };
              } else {
                playerRoleId = {
                  ...state.benchItems[i - 11],
                  focus: getDefaultFocus(
                    state.playerRoles,
                    state.benchItems[i - 11]?.playerRoleId,
                  ),
                };
              }
              tacticPlayerRoles.push(playerRoleId);
            }
          }
        }

        // An import without a tactic used to wipe `teamTactics` off the chosen
        // tactic, which then crashed on `teamTactics.formationId` below. Keep
        // whatever we already had and let the imported tactic override it.
        state.chosenTactic = {
          ...state.chosenTactic,
          ...tactic,
          playerRoles: tacticPlayerRoles,
        };
        const playersOnPitch = players.slice(0, 11);
        const playersOnBench = players.slice(11);

        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
          players: playersOnPitch as SquadBuilderPlayerType[],
          manager: state.squad.manager,
          playerRoleIds: state.chosenTactic.playerRoles.map(
            (pr) => pr.playerRoleId,
          ),
          formationId: state.chosenTactic.teamTactics.formationId,
          chemistryProfiles: state.chemistryProfiles,
          formations: state.formations,
          clubLinks: state.clubLinks,
          roleWeights: roleWeightsByRoleId(state.playerRoles),
        });

        const importedPlayers = playersOnPitch.map((p, index) => ({
          ...p,
          owned: true,
          chemstyleId: p?.chemStyle || 250,
          playerRoleId: state.chosenTactic?.playerRoles?.[index]?.playerRoleId,
        })) as SquadBuilderPlayerType[];
        state.squad = {
          ...state.squad,
          players: [...importedPlayers],
          chemistry,
          metaRating: teamMetaRating,
        };

        state.benchItems = playersOnBench.map((p, i) => {
          if (!p.resourceId) {
            return state.benchItems[i];
          }
          const metaRating = p?.metaRatings?.[0]?.metaRating;
          const playerRoleId = p?.metaRatings?.[0]?.playerRoleId;

          const eligiblePositions =
            state.playerRoles.find(
              (pr) => pr.id === p?.metaRatings?.[0]?.playerRoleId,
            )?.eligiblePositions || [];
          const position = eligiblePositions.find(
            (pos) =>
              pos === p.preferredPosition || p.possiblePositions.includes(pos),
          );

          return {
            ...p,
            owned: true,
            metaRating,
            playerRoleId,
            position,
          };
        }) as SquadBuilderPlayerType[];
      })
      .addCase(importSquad.rejected, (state) => {
        state.statusImportSquad = "rejected";
      })
      .addCase(importSquad.pending, (state) => {
        state.statusImportSquad = "pending";
      })
      .addCase(
        saveSquad.fulfilled,
        (state, action: PayloadAction<SaveSquadRes>) => {
          state.statusSaveSquad = "fulfilled";
          state.hasChangedTactic = false;

          if (!state.chosenTactic.id) {
            state.chosenTactic.id = action.payload.tacticId;
          }

          // save squad to list if it's not already there
          if (!state.savedSquads.find((ss) => ss.id === action.payload.id)) {
            state.savedSquads = [
              ...state.savedSquads,
              { id: action.payload.id, name: action.payload.name },
            ];
            state.loadedSaveSquadIndex = state.savedSquads.length - 1;
          }
        },
      )
      .addCase(saveSquad.rejected, (state) => {
        state.statusSaveSquad = "rejected";
      })
      .addCase(saveSquad.pending, (state) => {
        state.statusSaveSquad = "pending";
      })
      .addCase(
        fetchSquads.fulfilled,
        (state, action: PayloadAction<SavedSquad[]>) => {
          state.savedSquads = action.payload;
          state.statusFetchSavedSquads = "fulfilled";
        },
      )
      .addCase(fetchSquads.rejected, (state) => {
        state.statusFetchSavedSquads = "rejected";
      })
      .addCase(fetchSquads.pending, (state) => {
        state.statusFetchSavedSquads = "pending";
      })
      .addCase(
        fetchSpecificSquad.fulfilled,
        (
          state,
          action: PayloadAction<{
            squad: SavedOrSharedSquadResponseData;
            index?: number;
          }>,
        ) => {
          const { squad, index } = action.payload;
          state.statusLoadSpecficSquad = "fulfilled";
          if (index >= 0) {
            state.loadedSaveSquadIndex = index;
            state.hasChangedTactic = false;
          } else {
            state.loadedSaveSquadIndex = undefined;
          }

          const playerRoles = squad.tactic?.playerRoles ?? [];

          const playersOnPitch = squad.players.slice(0, 11);
          const benchPlayers = squad.players.slice(11);

          let tacticPlayerRoles = [];
          if (playerRoles.length < 23) {
            tacticPlayerRoles = playerRoles.map((p) => ({
              playerRoleId: p.playerRoleId,
              focus: getDefaultFocus(state.playerRoles, p.playerRoleId),
            }));

            for (let i = 11; i < 23; i++) {
              let playerRoleId = {};
              if (benchPlayers[i]?.resourceId) {
                playerRoleId = {
                  playerRoleId: benchPlayers[i]?.metaRatings?.[0]?.playerRoleId,
                  focus: getDefaultFocus(
                    state.playerRoles,
                    benchPlayers[i]?.metaRatings?.[0]?.playerRoleId,
                  ),
                };
              } else {
                playerRoleId = {
                  ...state.benchItems[i - 11],
                  focus: getDefaultFocus(
                    state.playerRoles,
                    state.benchItems[i - 11]?.playerRoleId,
                  ),
                };
              }
              tacticPlayerRoles.push(playerRoleId);
            }
          } else {
            tacticPlayerRoles = playerRoles;
          }
          const playersOnBench = [];
          for (let i = 0; i < 12; i++) {
            const p = benchPlayers[i];
            if (!p?.resourceId) {
              playersOnBench.push({
                playerRoleId: tacticPlayerRoles[i + 11]?.playerRoleId,
                position:
                  state.formations[state.chosenTactic.teamTactics.formationId]
                    .positions[i + 11],
              });
              continue;
            }

            const eligiblePositions =
              state.playerRoles.find(
                (a) => a.id === tacticPlayerRoles[i + 11]?.playerRoleId,
              )?.eligiblePositions || [];

            const position = eligiblePositions.find(
              (pos) =>
                pos === p.preferredPosition ||
                p.possiblePositions.includes(pos),
            );

            playersOnBench.push({
              ...p,
              metaRating: p?.metaRatings?.[0]?.metaRating,
              playerRoleId: tacticPlayerRoles[i + 11]?.playerRoleId,
              position,
            });
          }
          state.benchItems = playersOnBench;
          const players = [
            ...playersOnPitch.map((p, index) => ({
              ...p,
              playerRoleId: playerRoles[index]?.playerRoleId,
            })),
          ];

          const manager = {
            country: squad.manager?.countryId,
            league: squad.manager?.leagueId,
          };
          // A saved squad whose tactic lacks `teamTactics` would otherwise
          // crash on `teamTactics.formationId` below.
          state.chosenTactic = {
            ...state.chosenTactic,
            ...squad.tactic,
            teamTactics: {
              ...state.chosenTactic.teamTactics,
              ...squad.tactic?.teamTactics,
            },
            playerRoles: tacticPlayerRoles,
          };

          const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry(
            {
              players,
              manager,
              playerRoleIds: tacticPlayerRoles.map((pr) => pr.playerRoleId),
              formationId: state.chosenTactic.teamTactics.formationId,
              chemistryProfiles: state.chemistryProfiles,
              formations: state.formations,
              clubLinks: state.clubLinks,
              roleWeights: roleWeightsByRoleId(state.playerRoles),
            },
          );

          state.squad = {
            ...state.squad,
            players,
            chemistry,
            manager,
            metaRating: teamMetaRating,
          };
        },
      )
      .addCase(getTacticFromCode.fulfilled, (state, action) => {
        let playerRoles = action.payload.playerRoles.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));

        playerRoles = [
          ...playerRoles,
          ...state.benchItems.map((p) => ({
            playerRoleId: p.playerRoleId,
            focus: getDefaultFocus(state.playerRoles, p.playerRoleId),
          })),
        ];

        state.chosenTactic = {
          ...state.chosenTactic,
          ...action.payload,
          teamTactics: {
            ...state.chosenTactic.teamTactics,
            ...action.payload.teamTactics,
          },
          playerRoles,
        };
        state.chosenTactic.eaCode = action.payload.eaCode;
        state.squad.players = state.squad.players.map((p, index) => ({
          ...p,
          playerRoleId: playerRoles[index]?.playerRoleId,
        }));
        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
          players: state.squad.players,
          manager: state.squad.manager,
          playerRoleIds: playerRoles.map((pr) => pr.playerRoleId),
          formationId: state.chosenTactic.teamTactics.formationId,
          chemistryProfiles: state.chemistryProfiles,
          formations: state.formations,
          clubLinks: state.clubLinks,
          roleWeights: roleWeightsByRoleId(state.playerRoles),
        });

        state.squad = {
          ...state.squad,
          chemistry,
          metaRating: teamMetaRating,
        };
        state.statusInsertTacticCode = "fulfilled";
      })
      .addCase(getTacticFromCode.rejected, (state) => {
        state.statusInsertTacticCode = "rejected";
      })
      .addCase(getTacticFromCode.pending, (state) => {
        state.statusInsertTacticCode = "pending";
      })
      .addCase(getCodeFromTactic.fulfilled, (state, action) => {
        state.chosenTactic.eaCode = action.payload;
        state.statusUpdateTacticCode = "fulfilled";
      })
      .addCase(getCodeFromTactic.pending, (state) => {
        state.statusUpdateTacticCode = "pending";
      })
      .addCase(getCodeFromTactic.rejected, (state) => {
        state.statusUpdateTacticCode = "rejected";
      });
  },
  reducers: {
    updateFormation: (
      state,
      action: {
        payload: {
          formationId: string;
          isTemp?: boolean;
        };
      },
    ) => {
      if (action.payload.isTemp && action.payload.formationId === undefined) {
        state.tempSquad = setEmptySquad();
        state.tempFormation = undefined;
        return;
      }
      const lockedFormation = lockedFormationForLiveEvent(
        state.selectedLiveEventId,
      );
      if (lockedFormation && action.payload.formationId !== lockedFormation)
        return;
      // instead of index get formationId
      const playerRolesForFormation =
        state.formations[action.payload.formationId].playerRoleIds;

      const playersWithNewRoles = state.squad.players.map((p, index) => ({
        ...p,
        playerRoleId: playerRolesForFormation[index],
      }));
      const benchItemsWithNewRoles = state.benchItems.map((p, index) => ({
        ...p,
        playerRoleId: playerRolesForFormation[index + 11],
        position:
          state.formations[action.payload.formationId].positions[index + 11],
        metaRating:
          p?.metaRatings?.[0]?.playerRoleId ===
          playerRolesForFormation[index + 11]
            ? p?.metaRatings?.[0]?.metaRating
            : 0,
      }));

      const tacticPlayerRoles = [
        ...playersWithNewRoles.map((p) => p.playerRoleId),
        ...benchItemsWithNewRoles.map((p) => p.playerRoleId),
      ];

      if (action.payload.isTemp) {
        state.tempFormation = action.payload.formationId;
        state.tempSquad.players = playersWithNewRoles;
        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
          players: playersWithNewRoles,
          manager: state.squad.manager,
          playerRoleIds: tacticPlayerRoles,
          formationId: action.payload.formationId,
          chemistryProfiles: state.chemistryProfiles,
          formations: state.formations,
          clubLinks: state.clubLinks,
          roleWeights: roleWeightsByRoleId(state.playerRoles),
        });

        state.tempSquad = {
          ...state.tempSquad,
          chemistry,
          metaRating: teamMetaRating,
        };
      } else {
        state.tempSquad = setEmptySquad();
        state.tempFormation = undefined;
        state.squad.players = playersWithNewRoles;
        state.benchItems = benchItemsWithNewRoles;
        state.chosenTactic = {
          ...state.chosenTactic,
          playerRoles: tacticPlayerRoles.map((pr) => ({
            playerRoleId: pr,
            focus: getDefaultFocus(state.playerRoles, pr),
          })),
          teamTactics: {
            ...state.chosenTactic.teamTactics,
            formationId: action.payload.formationId,
          },
        };

        const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
          players: playersWithNewRoles,
          manager: state.squad.manager,
          playerRoleIds: tacticPlayerRoles,
          formationId: action.payload.formationId,
          chemistryProfiles: state.chemistryProfiles,
          formations: state.formations,
          clubLinks: state.clubLinks,
          roleWeights: roleWeightsByRoleId(state.playerRoles),
        });
        state.squad = {
          ...state.squad,
          chemistry,
          metaRating: teamMetaRating,
        };
      }
    },
    internalMovePlayer(
      state,
      action: { payload: { from: number; to: number } },
    ) {
      const { chosenTactic, squad, benchItems } = state;
      const { players } = squad;
      const { from, to } = action.payload;

      let newPlayers = [...players];
      let newBenchItems = [...benchItems];

      const fromIsBench = from > 10;
      const toIsBench = to > 10;

      if (fromIsBench && toIsBench) {
        // Bench to bench
        const draggingPlayer = newBenchItems[from - 11];
        const potentialPlayer = newBenchItems[to - 11];
        newBenchItems[from - 11] = {
          ...potentialPlayer,
          playerRoleId: draggingPlayer.playerRoleId,
          position: draggingPlayer.position,
          chemistry: 0,
        };
        newBenchItems[to - 11] = {
          ...draggingPlayer,
          position: potentialPlayer.position,
          playerRoleId: potentialPlayer.playerRoleId,
        };
      } else if (!fromIsBench && !toIsBench) {
        // Squad to squad
        const draggingPlayer = newPlayers[from];
        newPlayers[from] = {
          ...newPlayers[to],
          playerRoleId: draggingPlayer.playerRoleId,
          // chemIndex: from,
        };
        newPlayers[to] = {
          ...draggingPlayer,
          playerRoleId: newPlayers[to].playerRoleId,
          // chemIndex: to,
        };
      } else if (fromIsBench && !toIsBench) {
        // Bench to squad
        const draggingPlayer = newBenchItems[from - 11];
        newBenchItems[from - 11] = {
          ...newPlayers[to],
          playerRoleId: draggingPlayer.playerRoleId,
          position: draggingPlayer.position,
        };
        newPlayers[to] = {
          ...draggingPlayer,
          playerRoleId: newPlayers[to].playerRoleId,
          // chemIndex: to,
        };
      } else {
        // Squad to bench
        const draggingPlayer = newPlayers[from];
        newPlayers[from] = {
          ...newBenchItems[to - 11],
          playerRoleId: draggingPlayer.playerRoleId,
          // chemIndex: from,
        };
        newBenchItems[to - 11] = {
          ...draggingPlayer,
          playerRoleId: newBenchItems[to - 11].playerRoleId,
          position: newBenchItems[to - 11].position,
          chemistry: 0,
        };
      }

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
        players: newPlayers,
        manager: state.squad.manager,
        playerRoleIds: state.chosenTactic.playerRoles.map(
          (pr) => pr.playerRoleId,
        ),
        formationId: state.chosenTactic.teamTactics.formationId,
        chemistryProfiles: state.chemistryProfiles,
        formations: state.formations,
        clubLinks: state.clubLinks,
        roleWeights: roleWeightsByRoleId(state.playerRoles),
      });
      state.squad = {
        ...state.squad,
        players: newPlayers,
        chemistry,
        metaRating: teamMetaRating,
      };
      state.benchItems = newBenchItems;
    },
    addPlayerOrChangeRole(
      state,
      action: {
        payload: {
          playerRoleId?: number;
          focus?: string;
          player?: SquadBuilderPlayerType;
          temp?: boolean;
          metaRatings?: MetaRating[];
          position?: string;
        };
      },
    ) {
      if (state.changingPitchItemOnIndex > 10) {
        if (action.payload.temp) {
          return;
        }
        const player =
          action.payload.player ||
          state.benchItems[state.changingPitchItemOnIndex - 11];

        let metaRating = 0;
        let metaRatings = [];

        if (
          action.payload.position &&
          player?.possiblePositions?.includes(action.payload.position)
        ) {
          metaRatings = action.payload.metaRatings ?? [];
          metaRating = action.payload.metaRatings?.[0]?.metaRating || 0;
        } else if (!action.payload.position) {
          metaRatings = player?.metaRatings ?? [];
          metaRating = player?.metaRatings?.[0]?.metaRating || 0;
        }
        const playerRoleId =
          action.payload.playerRoleId ||
          state.benchItems[state.changingPitchItemOnIndex - 11]?.playerRoleId;

        state.benchItems[state.changingPitchItemOnIndex - 11] = {
          ...(player?.resourceId && player),
          position:
            action.payload.position ||
            state.benchItems[state.changingPitchItemOnIndex - 11]?.position,
          playerRoleId,
          ...(metaRatings && {
            metaRatings,
          }),
        };
        state.chosenTactic.playerRoles[state.changingPitchItemOnIndex] = {
          playerRoleId:
            action.payload.playerRoleId ||
            state.benchItems[state.changingPitchItemOnIndex - 11]?.playerRoleId,
          focus: getDefaultFocus(state.playerRoles, playerRoleId),
        };
        return;
      }
      const { squad, chosenTactic } = state;
      const { manager, players } = squad;
      let newPlayers = [...players];

      let player: SquadBuilderPlayerType;
      if (action.payload.player) {
        // adding player
        player = {
          ...action.payload.player,
          playerRoleId:
            state.squad.players[state.changingPitchItemOnIndex]?.playerRoleId,
        };

        // If the new player was already on the bench, handle swap or clear
        if (player.resourceId && !action.payload.temp) {
          const benchIndex = state.benchItems.findIndex(
            (benchPlayer) => benchPlayer.resourceId === player.resourceId,
          );
          if (benchIndex !== -1) {
            const currentPitchPlayer =
              state.squad.players[state.changingPitchItemOnIndex];
            // If there's a player on the pitch, swap them with the bench player
            if (currentPitchPlayer?.resourceId) {
              // Swap: move pitch player to bench, preserving bench slot's playerRoleId and position
              // Remove pitch-specific properties (chemstyleId, chemistry) and use first metaRatings entry
              const {
                chemstyleId,
                chemistry: _chemistry,
                ...playerWithoutPitchProps
              } = currentPitchPlayer;
              state.benchItems[benchIndex] = {
                ...playerWithoutPitchProps,
                playerRoleId: state.benchItems[benchIndex].playerRoleId,
                position: state.benchItems[benchIndex].position,
                chemistry: currentPitchPlayer.metaRatings?.[0]?.chemistry || 0,
              };
            } else {
              // No player on pitch, just clear the bench slot
              state.benchItems[benchIndex] = {
                ...emptyPlayer,
                playerRoleId: state.benchItems[benchIndex].playerRoleId,
                position: state.benchItems[benchIndex].position,
              };
            }
          }
        }
      } else if (action.payload.playerRoleId) {
        // changing player role
        player = {
          ...state.squad.players[state.changingPitchItemOnIndex],
          playerRoleId: action.payload.playerRoleId,
          metaRatings: action.payload.metaRatings,
        };
      } else if (action.payload.metaRatings) {
        // changing chemstyle
        player = {
          ...state.squad.players[state.changingPitchItemOnIndex],
          metaRatings: action.payload.metaRatings,
        };
      }
      // adding the new player before updating meta rating and chemistry
      newPlayers[state.changingPitchItemOnIndex] = {
        ...player,
      };

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
        players: newPlayers,
        manager: state.squad.manager,
        playerRoleIds: state.chosenTactic.playerRoles.map(
          (pr) => pr.playerRoleId,
        ),
        formationId: state.chosenTactic.teamTactics.formationId,
        chemistryProfiles: state.chemistryProfiles,
        formations: state.formations,
        clubLinks: state.clubLinks,
        roleWeights: roleWeightsByRoleId(state.playerRoles),
      });

      const playerChemistry =
        chemistry.playersChemistry[state.changingPitchItemOnIndex];

      let chemStyleId = 0;
      try {
        chemStyleId =
          findMetaRating({
            metaRatings: player.metaRatings,
            playerRoleId: player.playerRoleId,
            chemistry: playerChemistry,
          })?.chemstyleId || 0;
      } catch (error) {
        chemStyleId = 0;
        captureMessage(error instanceof Error ? error.message : String(error), {
          level: "error",
          tags: {
            component: "addPlayerOrChangeRole chemStyleId",
          },
        });
      }

      player = {
        ...player,
        chemistry: playerChemistry,
        chemstyleId: chemStyleId,
      };
      // adding the updated player to the squad
      newPlayers[state.changingPitchItemOnIndex] = {
        ...player,
      };

      if (action.payload.temp) {
        const tempPlayers = setEmptySquad().players;
        tempPlayers[state.changingPitchItemOnIndex] = { ...player };
        state.tempSquad = {
          ...squad,
          players: tempPlayers,
          chemistry,
          metaRating: teamMetaRating,
        };
      } else {
        state.squad = {
          ...squad,
          players: newPlayers,
          chemistry,
          metaRating: teamMetaRating,
        };
        state.tempSquad = setEmptySquad();
      }

      // updating tactic specifically
      if (
        (action.payload.playerRoleId || action.payload.focus) &&
        !action.payload.player &&
        !action.payload.temp
      ) {
        let focus: string;
        if (action.payload.focus) {
          focus = action.payload.focus;
        } else {
          // find default focus for the new player role
          focus = getDefaultFocus(state.playerRoles, player.playerRoleId);
        }

        state.chosenTactic.playerRoles[state.changingPitchItemOnIndex] = {
          playerRoleId: player.playerRoleId,
          focus,
        };
      }
    },
    removePlayer(
      state,
      action: {
        payload: {
          index?: number;
          temp?: boolean;
        };
      },
    ) {
      const temp = action.payload.temp;
      if (temp) {
        state.tempSquad = setEmptySquad();
        return;
      }

      if (action.payload.index > 10) {
        state.benchItems[action.payload.index - 11] = {
          ...emptyPlayer,
          playerRoleId:
            state.benchItems[action.payload.index - 11]?.playerRoleId,
          position: state.benchItems[action.payload.index - 11]?.position,
        };
        return;
      }

      const { squad } = state;
      const { players, manager } = squad;
      const newPlayers = [...players];
      const index =
        action.payload.index >= 0
          ? action.payload.index
          : state.changingPitchItemOnIndex;

      newPlayers[index] = {
        ...emptyPlayer,
        playerRoleId: state.squad.players[index]?.playerRoleId,
      };

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
        players: newPlayers,
        manager,
        playerRoleIds: state.chosenTactic.playerRoles.map(
          (pr) => pr.playerRoleId,
        ),
        formationId: state.chosenTactic.teamTactics.formationId,
        chemistryProfiles: state.chemistryProfiles,
        formations: state.formations,
        clubLinks: state.clubLinks,
        roleWeights: roleWeightsByRoleId(state.playerRoles),
      });

      const newSquad = {
        ...state.squad,
        players: newPlayers,
        chemistry,
        metaRating: teamMetaRating,
      };
      state.squad = newSquad;
    },
    resetRole(state, action: { payload: number }) {
      const chosenTactic = state.chosenTactic;
      state.squad.players[action.payload] = {
        ...emptyPlayer,
        playerRoleId:
          state.formations[chosenTactic.teamTactics.formationId]?.playerRoleIds[
            action.payload
          ],
      };
    },
    selectChemStyle(
      state,
      action: {
        payload: {
          chemstyleId: number;
          metaRatings: MetaRating[];
        };
      },
    ) {
      // `squad.players` only holds the 11 pitch slots — bench slots live in
      // `benchItems` at `index - 11`, same as `addPlayerOrChangeRole` does.
      // Writing straight to `players[index]` threw for every bench player.
      const target = squadOrBenchItem(state, state.changingPitchItemOnIndex);
      if (!target) {
        return;
      }
      target.chemstyleId = action.payload.chemstyleId;
      target.metaRatings = action.payload.metaRatings;
    },
    addSquad(
      state,
      action: {
        payload: {
          index: number;
          temp?: boolean;
        };
      },
    ) {
      const index = action.payload.index;
      const temp = action.payload.temp;
      const { chosenTactic } = state;
      const builtSquad = state.builtSquads[index];

      // Hovering or clicking a suggested squad that is no longer in
      // `builtSquads` (the list is replaced while the panel is open) used to
      // spread `undefined` into an empty object and then crash on
      // `chosenSquad.tactic.playerRoles`.
      if (!builtSquad) {
        return;
      }
      const chosenSquad = { ...builtSquad };

      const { manager, players } = chosenSquad;

      // Fall back to the chosen tactic when the suggested squad carries no
      // tactic of its own, rather than reading through a missing one.
      const useSquadTactic = state.isBestFormationOn && !!chosenSquad.tactic;

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
        players: players,
        manager: manager,
        playerRoleIds: useSquadTactic
          ? chosenSquad.tactic.playerRoles.map((pr) => pr.playerRoleId)
          : state.chosenTactic.playerRoles.map((pr) => pr.playerRoleId),
        formationId: useSquadTactic
          ? chosenSquad.tactic.teamTactics.formationId
          : chosenTactic.teamTactics.formationId,
        chemistryProfiles: state.chemistryProfiles,
        formations: state.formations,
        clubLinks: state.clubLinks,
        roleWeights: roleWeightsByRoleId(state.playerRoles),
      });
      const playersOnPitch: SquadBuilderPlayerType[] = [];
      const playersOnBench: SquadBuilderPlayerType[] = [...state.benchItems];

      for (let i = 0; i < 23; i++) {
        if (i < 11) {
          playersOnPitch.push({
            ...chosenSquad.players[i],
            chemistry: chemistry.playersChemistry[i],
            // playerRoleId: state.squad.players[i].playerRoleId, // TODO: check if this is needed
          });
          continue;
        }

        const benchIndex = i - 11;

        if (i < 18) {
          const suggestedBenchPlayer = chosenSquad.players[i];
          const existingBenchPlayer = state.benchItems[benchIndex];
          const baseBenchPlayer =
            suggestedBenchPlayer ?? existingBenchPlayer ?? emptyPlayer;

          const tacticPlayerRoleId =
            chosenSquad.tactic?.playerRoles?.[i]?.playerRoleId;
          const tacticPosition = state.playerRoles.find(
            (playerRole) => playerRole.id === tacticPlayerRoleId,
          )?.position;

          const resolvedPlayerRoleId = state.isBestFormationOn
            ? (pickDefined(
                tacticPlayerRoleId,
                existingBenchPlayer?.playerRoleId,
                suggestedBenchPlayer?.playerRoleId,
              ) ?? 0)
            : (pickDefined(
                existingBenchPlayer?.playerRoleId,
                suggestedBenchPlayer?.playerRoleId,
              ) ?? 0);

          const resolvedPosition = state.isBestFormationOn
            ? pickDefined(
                tacticPosition,
                existingBenchPlayer?.position,
                suggestedBenchPlayer?.position,
              )
            : pickDefined(
                existingBenchPlayer?.position,
                suggestedBenchPlayer?.position,
              );

          playersOnBench[benchIndex] = {
            ...baseBenchPlayer,
            chemistry: chemistry.playersChemistry[i],
            playerRoleId: resolvedPlayerRoleId,
            position: resolvedPosition,
          };

          continue;
        }

        // use the reserves from before the squad was built
        playersOnBench[benchIndex] = {
          ...state.benchItems[benchIndex],
          playerRoleId: state.benchItems[benchIndex].playerRoleId,
          position: state.benchItems[benchIndex].position,
        };
      }

      if (temp) {
        state.tempSquad = {
          ...chosenSquad,
          players: playersOnPitch,
          chemistry,
          metaRating: teamMetaRating,
          manager,
        };
        state.tempBenchItems = playersOnBench;
        if (state.isBestFormationOn) {
          state.tempChosenTactic = chosenSquad.tactic;
          state.tempFormation = chosenSquad.tactic.teamTactics.formationId;
        }
      } else {
        state.squad = {
          ...chosenSquad,
          players: playersOnPitch,
          chemistry,
          metaRating: teamMetaRating,
          manager,
        };
        if (state.isBestFormationOn) {
          state.chosenTactic = chosenSquad.tactic;
        }
        state.benchItems = playersOnBench;
        state.statusBuildSquad = "idle";
        state.tempSquad = setEmptySquad();
        state.tempBenchItems = [];
        state.isBestFormationOn = false;
        state.tempChosenTactic = undefined;
        state.tempFormation = undefined;
      }
    },
    removeTempSquad(state) {
      state.tempSquad = setEmptySquad();
      state.tempBenchItems = [];
      state.tempChosenTactic = undefined;
      state.tempFormation = undefined;
    },
    clearSquad(state) {
      state.playerFilters = setEmptyPlayerFilters(
        extractPlayStylesFilterOptions(state.playerFilters),
      );
      state.squad = setEmptySquad(state.chosenTactic.playerRoles.slice(0, 11));
      state.benchItems = setEmptyBench({
        playerRoles: state.benchItems.map((p) => ({
          playerRoleId: p.playerRoleId,
          position: p.position,
        })),
      });
      state.tempSquad = setEmptySquad();
      state.tempBenchItems = [];
      state.loadedSaveSquadIndex = undefined;
      state.statusBuildSquad = "idle";
    },
    exitPickSquad(state) {
      state.statusBuildSquad = "idle";
    },
    setSaveSquadStatusToIdle(state) {
      state.statusSaveSquad = "idle";
    },
    updateChangingPitchItemOnIndex(state, action: { payload: number }) {
      state.changingPitchItemOnIndex = action.payload;
    },
    updateBudget(state, action: { payload: number }) {
      state.budget = action.payload;
    },
    updateScope(state, action: { payload: Scope }) {
      state.scope = action.payload;
    },
    updateExcludeIconsHeroes(state, action: { payload: boolean }) {
      state.excludeIconsHeroes = action.payload;
    },
    updateSelectedLiveEventId(state, action: { payload: number | undefined }) {
      state.selectedLiveEventId = action.payload;
    },
    updateIncludeLoanPlayers(state, action: { payload: boolean }) {
      state.includeLoanPlayers = action.payload;
      // if state.scope is both, we just keep the scope as both
      if (state.scope === "market") {
        state.scope = "club";
      }
    },
    updateMinRating(state, action: { payload: number }) {
      state.ratingFilter = {
        ...state.ratingFilter,
        min: action.payload,
      };
    },
    updateMaxRating(state, action: { payload: number }) {
      state.ratingFilter = {
        ...state.ratingFilter,
        max: action.payload,
      };
    },
    updateLoadedSquadIndex(state, action: { payload: number }) {
      state.loadedSaveSquadIndex = action.payload;
      state.statusSaveSquad = "idle";
    },
    updateManager(
      state,
      action: { payload: { id: number; type: "league" | "country" } },
    ) {
      const newManager = { ...state.squad.manager };
      if (action.payload.type === "league") {
        if (state.squad.manager.league === action.payload.id) {
          newManager["league"] = null;
        } else {
          newManager["league"] = action.payload.id;
        }
      } else {
        if (state.squad.manager.country === action.payload.id) {
          newManager["country"] = null;
        } else {
          newManager["country"] = action.payload.id;
        }
      }
      state.squad.manager = {
        ...newManager,
      };

      const { chosenTactic } = state;

      const { chemistry, teamMetaRating } = calculateMetaRatingAndChemistry({
        players: state.squad.players,
        manager: newManager,
        playerRoleIds: chosenTactic.playerRoles.map((pr) => pr.playerRoleId),
        formationId: chosenTactic.teamTactics.formationId,
        chemistryProfiles: state.chemistryProfiles,
        formations: state.formations,
        clubLinks: state.clubLinks,
        roleWeights: roleWeightsByRoleId(state.playerRoles),
      });

      state.squad = {
        ...state.squad,
        chemistry,
        metaRating: teamMetaRating,
      };
    },
    updatePlayersMetaRatingsForAllRoles(
      state,
      action: PayloadAction<{
        metaRatings: MetaRating[];
        changingPlayerIndex: number;
      }>,
    ) {
      const { metaRatings, changingPlayerIndex } = action.payload;

      // Same pitch/bench split as `selectChemStyle` — a bench index is not a
      // valid `squad.players` index.
      const target = squadOrBenchItem(state, changingPlayerIndex);
      if (!target) {
        return;
      }
      target.metaRatings = metaRatings;

      // todo: update team meta rating.
    },

    updateCustomFilter: (state, action: PayloadAction<UpdateFilterType>) => {
      const { key } = action.payload;

      const playerIndex = state.changingPitchItemOnIndex;

      const filter = state.playerFilters[playerIndex]?.find(
        (f) => f.key === key,
      );

      if (filter?.type === "RANGE_FILTER") {
        const { min, max } = action.payload;
        if (min !== undefined) filter.minValue = min;
        if (max !== undefined) filter.maxValue = max;
      } else if (filter?.type === "SELECTOR_FILTER") {
        const { value } = action.payload;
        const isSelected = filter.selectedOptions.includes(value);

        if (isSelected)
          filter.selectedOptions = filter.selectedOptions.filter(
            (so) => so !== value,
          );
        else filter.selectedOptions.push(value);
      }
    },
    clearCustomFilter(state, action: PayloadAction<string>) {
      const filter = state.playerFilters[state.changingPitchItemOnIndex].find(
        (f) => f.key === action.payload,
      );

      if (filter?.type === "RANGE_FILTER") {
        filter.minValue = null;
        filter.maxValue = null;
      } else if (filter?.type === "SELECTOR_FILTER") {
        filter.selectedOptions = [];
      }
    },
    clearPlayerFilters(state, action: PayloadAction<number>) {
      state.playerFilters[action.payload] = setEmptyAttributes(
        extractPlayStylesFilterOptions(state.playerFilters),
      );
    },
    clearFilter(state, action: PayloadAction<FilterKey>) {
      switch (action.payload) {
        case "budget":
          state.budget = Number.MAX_VALUE;
          break;
        case "country":
          state.selectedCountries = [];
          break;
        case "league":
          state.selectedLeagues = [];
          break;
        case "clubs":
          state.selectedClubs = [];
          break;
        case "rarity":
          state.selectedVersions = [];
          break;
        case "market":
          state.scope = "market";
          break;
        case "both":
          state.scope = "market";
          break;
        case "club":
          state.scope = "market";
          break;
        case "playerFilter":
          state.playerFilters = setEmptyPlayerFilters(
            extractPlayStylesFilterOptions(state.playerFilters),
          );
          break;
        case "pastAndPresentClub":
          state.selectedPnPs = [];
          break;
        case "group":
          state.selectedCountries = [];
          state.selectedLeagues = [];
          state.selectedClubs = [];
          state.selectedVersions = [];
          state.selectedPnPs = [];
          state.selectedMetals = [];
          break;
        default:
          break;
      }
    },
    // Every filter that hasDefaultFiltersSelector reads, back to its default.
    clearAllFilters(state) {
      state.budget = Number.MAX_VALUE;
      state.scope = "market";
      state.selectedCountries = [];
      state.selectedLeagues = [];
      state.selectedClubs = [];
      state.selectedVersions = [];
      state.selectedPnPs = [];
      state.selectedMetals = [];
      state.excludeIconsHeroes = false;
      state.includeLoanPlayers = false;
      state.selectedLiveEventId = undefined;
      state.playerFilters = setEmptyPlayerFilters(
        extractPlayStylesFilterOptions(state.playerFilters),
      );
    },
    updateSelectedCountries(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedCountries = [];
        return;
      }
      const id = action.payload;
      if (state.selectedCountries.includes(id))
        state.selectedCountries = state.selectedCountries.filter(
          (c) => c !== id,
        );
      else state.selectedCountries = [...state.selectedCountries, id];
    },
    updateSelectedLeagues(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedLeagues = [];
        return;
      }
      const id = action.payload;
      if (state.selectedLeagues.includes(id))
        state.selectedLeagues = state.selectedLeagues.filter((c) => c !== id);
      else state.selectedLeagues = [...state.selectedLeagues, id];
    },
    updateSelectedClubs(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedClubs = [];
        return;
      }
      const id = action.payload;
      if (state.selectedClubs.includes(id))
        state.selectedClubs = state.selectedClubs.filter((c) => c !== id);
      else state.selectedClubs = [...state.selectedClubs, id];
    },
    updateSelectedPnPs(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedPnPs = [];
        return;
      }
      const id = action.payload;
      if (state.selectedPnPs.includes(id))
        state.selectedPnPs = state.selectedPnPs.filter((c) => c !== id);
      else state.selectedPnPs = [...state.selectedPnPs, id];
    },
    updateSelectedVersions(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedVersions = [];
        return;
      }
      const id = action.payload;
      if (state.selectedVersions.includes(id))
        state.selectedVersions = state.selectedVersions.filter((c) => c !== id);
      else state.selectedVersions = [...state.selectedVersions, id];
    },
    updateSelectedMetals(state, action: { payload: number }) {
      if (action.payload === -1) {
        state.selectedMetals = [];
        return;
      }
      const id = action.payload;
      if (state.selectedMetals.includes(id))
        state.selectedMetals = state.selectedMetals.filter((c) => c !== id);
      else state.selectedMetals = [...state.selectedMetals, id];
    },
    sortBuiltSquads(state, action: { payload: "meta-rating" | "chemistry" }) {
      state.builtSquads = [...state.builtSquads].sort((a, b) => {
        if (action.payload === "meta-rating") {
          return b.metaRating - a.metaRating;
        } else {
          return b.suggestedSquadChemistry - a.suggestedSquadChemistry;
        }
      });
    },
    switchRightPanelMode(state, action: { payload: RightPanelMode }) {
      state.rightPanelMode = action.payload;
    },
    switchLeftPanelMode(state, action: { payload: LeftPanelMode }) {
      state.leftPanelMode = action.payload;
    },
    setChosenTactic(
      state,
      action: {
        payload: {
          tactic: Tactic;
          fromTacticsPage: boolean;
          initialLoad?: boolean;
        };
      },
    ) {
      const lockedFormation = lockedFormationForLiveEvent(
        state.selectedLiveEventId,
      );
      if (
        lockedFormation &&
        action.payload.tactic.teamTactics.formationId !== lockedFormation
      )
        return;

      let playerRoles: { playerRoleId: number; focus: string }[];

      if (action.payload.fromTacticsPage) {
        playerRoles = action.payload.tactic.playerRoles?.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));
        state.benchItems.forEach((bi) => {
          playerRoles.push({
            playerRoleId: bi.playerRoleId,
            focus: getDefaultFocus(state.playerRoles, bi.playerRoleId),
          });
        });
      } else if (
        state.chosenTactic?.teamTactics?.formationId !==
        action.payload.tactic.teamTactics.formationId
      ) {
        playerRoles = state.formations[
          action.payload.tactic.teamTactics.formationId
        ].playerRoleIds.map((pr) => ({
          playerRoleId: pr,
          focus: getDefaultFocus(state.playerRoles, pr),
        }));
      } else {
        playerRoles = action.payload.tactic.playerRoles.map((pr) => ({
          playerRoleId: pr.playerRoleId,
          focus: pr.focus,
        }));
      }

      let defensiveApproach =
        action.payload.tactic.teamTactics.defensiveApproach;
      let lineHeight = action.payload.tactic.teamTactics.lineHeight;

      if (
        action.payload.tactic.teamTactics.lineHeight !==
        state.chosenTactic.teamTactics.lineHeight
      ) {
        defensiveApproach = defensiveApproachForLineHeight(lineHeight);
      } else if (
        action.payload.tactic.teamTactics.defensiveApproach !==
        state.chosenTactic.teamTactics.defensiveApproach
      ) {
        if (action.payload.tactic.teamTactics.defensiveApproach === "Deep") {
          lineHeight = 25;
        } else if (
          action.payload.tactic.teamTactics.defensiveApproach === "Balanced"
        ) {
          lineHeight = 50;
        } else if (
          action.payload.tactic.teamTactics.defensiveApproach === "High"
        ) {
          lineHeight = 70;
        } else {
          lineHeight = 95;
        }
      }
      state.squad.players = [
        ...state.squad.players.map((p, index) => ({
          ...p,
          playerRoleId: playerRoles[index].playerRoleId,
        })),
      ];
      if (action.payload.fromTacticsPage) {
        const { id, ...rest } = action.payload.tactic;
        state.chosenTactic = {
          ...rest,
          playerRoles,
        };
      } else if (!action.payload.initialLoad) {
        state.chosenTactic = {
          ...action.payload.tactic,
          teamTactics: {
            ...action.payload.tactic.teamTactics,
            defensiveApproach,
            lineHeight,
          },
          playerRoles,
        };
      }
    },
    updateTacticInsertStatus(state, action: { payload: APIStatus }) {
      state.statusInsertTacticCode = action.payload;
    },
    clearRecommendedPlayers(state) {
      state.recommendedPlayers = [];
    },
    loadPlayStyleFilterOptions(
      state,
      action: {
        payload: {
          playStyleFilter: SelectorFilterType;
          playStylePlusFilter: SelectorFilterType;
        };
      },
    ) {
      state.playerFilters.forEach((filter) => {
        const convertedPlaystyleOptions = Object.values(
          action.payload.playStyleFilter.options,
        ).map((playStyle) => ({
          id: playStyle.id,
          label: playStyle.label,
          value: playStyle.id,
          category: playStyle.category,
        }));

        const convertedPlaystylePlusOptions = Object.values(
          action.payload.playStylePlusFilter.options,
        ).map((playStyle) => ({
          id: playStyle.id,
          label: playStyle.label,
          value: playStyle.id,
          category: playStyle.category,
        }));
        filter.forEach((f) => {
          if (f.key === "playStyles" && "options" in f) {
            f.options = convertedPlaystyleOptions;
          } else if (f.key === "playStylesPlus" && "options" in f) {
            f.options = convertedPlaystylePlusOptions;
          }
        });
      });
    },
    updateBestFormation(state, action: { payload: boolean }) {
      const isBestFormationOn =
        action.payload &&
        !lockedFormationForLiveEvent(state.selectedLiveEventId);
      state.isBestFormationOn = isBestFormationOn;
      if (isBestFormationOn) {
        state.bestFormationIncludedPlayers = state.squad.players
          .filter((player) => player.resourceId)
          .map((player) => player);
      } else {
        state.bestFormationIncludedPlayers = [];
      }
    },
    addBestFormationIncludedPlayer(
      state,
      action: {
        payload: SearchedPlayer;
      },
    ) {
      state.bestFormationIncludedPlayers.push(action.payload);
    },
    removeBestFormationIncludedPlayer(state, action: { payload: number }) {
      state.bestFormationIncludedPlayers =
        state.bestFormationIncludedPlayers.filter(
          (player) => player.resourceId !== action.payload,
        );
    },
    updateHasChangedTactic(
      state,
      action: { payload: { name: string; description: string } },
    ) {
      state.hasChangedTactic = true;
      state.chosenTactic = setCustomTactic(state.chosenTactic, action.payload);
    },
    updateStatusesToIdle(state) {
      state.statusImportSquad = "idle";
      state.statusLoadSpecficSquad = "idle";
    },
  },
});

export const defaultSquadBuilderSelector = (state: IRootState) =>
  state.defaultSquadBuilderReducer;

/** Picks the live event and, when it only allows one formation, moves the
    squad onto it and turns Best Formation off. */
export const selectLiveEvent =
  (liveEventId: number | undefined) =>
  (dispatch: AppDispatch, getState: () => IRootState) => {
    const { updateSelectedLiveEventId, updateBestFormation, updateFormation } =
      defaultSquadBuilderSlice.actions;
    dispatch(updateSelectedLiveEventId(liveEventId));
    const lockedFormation = lockedFormationForLiveEvent(liveEventId);
    if (!lockedFormation) return;
    dispatch(updateBestFormation(false));
    const { formations, chosenTactic } = getState().defaultSquadBuilderReducer;
    if (
      formations[lockedFormation] &&
      chosenTactic.teamTactics.formationId !== lockedFormation
    )
      dispatch(updateFormation({ formationId: lockedFormation }));
  };

export const teamValueSelector = () => (state: IRootState) => {
  let squad: Squad = setEmptySquad();
  let tempSquad: Squad = setEmptySquad();

  squad = state.defaultSquadBuilderReducer.squad;
  tempSquad = state.defaultSquadBuilderReducer.tempSquad;

  const { players: tempPlayers } = tempSquad;
  const { players } = squad;

  let teamValue = 0;
  let tempTeamValue = 0;
  let ownedTeamValue = 0;
  let tempOwnedTeamValue = 0;
  for (let i = 0; i < 11; i++) {
    const player = players[i];
    const tempPlayer = tempPlayers[i];

    teamValue += getCoinValue(player?.priceInfo);
    tempTeamValue += getCoinValue(tempPlayer?.priceInfo);
    ownedTeamValue += player?.owned ? getCoinValue(player?.priceInfo) : 0;
    tempOwnedTeamValue += tempPlayer?.owned
      ? getCoinValue(tempPlayer?.priceInfo)
      : 0;
  }

  return {
    teamValue,
    tempTeamValue,
    ownedTeamValue,
    tempOwnedTeamValue,
  };
};

export const numberOfPlayersWithFiltersSelector = createSelector(
  [(state: IRootState) => state.defaultSquadBuilderReducer.playerFilters],
  (playerFilters) => {
    let count = 0;

    for (const filter of playerFilters) {
      for (const f of filter) {
        if (f.type === "SELECTOR_FILTER") {
          if (f.selectedOptions.length > 0) {
            count++;
            break; // Found a filter for this player, move to next player
          }
        } else if (f.minValue || f.maxValue) {
          count++;
          break; // Found a filter for this player, move to next player
        }
      }
    }

    return count;
  },
);

/**
 * True while no squad filter has been touched: the state a first visit lands
 * in. The Customize Squad Suggestions dialog opens on a build from here.
 */

/** How many filters clearAllFilters would reset; shown on the button. */
export const activeFilterCountSelector = createSelector(
  [
    numberOfPlayersWithFiltersSelector,
    (state: IRootState) => state.defaultSquadBuilderReducer,
  ],
  (numberOfPlayersWithFilters, sb) =>
    numberOfPlayersWithFilters +
    (sb.budget === Number.MAX_VALUE ? 0 : 1) +
    (sb.scope === "market" ? 0 : 1) +
    sb.selectedCountries.length +
    sb.selectedLeagues.length +
    sb.selectedClubs.length +
    sb.selectedPnPs.length +
    sb.selectedVersions.length +
    sb.selectedMetals.length +
    (sb.excludeIconsHeroes ? 1 : 0) +
    (sb.includeLoanPlayers ? 1 : 0) +
    (sb.selectedLiveEventId === undefined ? 0 : 1),
);

/**
 * True while no squad filter has been touched: the state a first visit lands
 * in. The Customize Squad Suggestions dialog opens on a build from here.
 */
export const hasDefaultFiltersSelector = createSelector(
  [activeFilterCountSelector],
  (count) => count === 0,
);

// Memoized selector for better performance
export const activeFiltersSelector: Selector<IRootState, ActiveFilter[]> =
  createSelector(
    [
      numberOfPlayersWithFiltersSelector,
      (state: IRootState) => state.defaultSquadBuilderReducer.scope,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedCountries,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedLeagues,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedClubs,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedPnPs,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedMetals,
      (state: IRootState) => state.defaultSquadBuilderReducer.selectedVersions,
      (state: IRootState) => state.defaultSquadBuilderReducer.budget,
    ],
    (
      numberOfPlayersWithFilters,
      scope,
      selectedCountries,
      selectedLeagues,
      selectedClubs,
      selectedPnPs,
      selectedMetals,
      selectedVersions,
      budget,
    ) => {
      const activeFilters: ActiveFilter[] = [];

      // Budget filter
      if (budget < Number.MAX_VALUE) {
        activeFilters.push({
          key: "budget",
          value: convertSBCPrice(budget),
        });
      }

      // Player filters
      if (numberOfPlayersWithFilters > 0) {
        activeFilters.push({
          key: "playerFilter",
          value: numberOfPlayersWithFilters.toString(),
        });
      }

      // Scope filter
      if (scope) {
        activeFilters.push({ key: scope, value: "-" });
      }

      // Helper function for adding filter counts
      const addFilterCount = (key: FilterKey, filters: any[]) => {
        if (filters?.length > 0) {
          activeFilters.push({ key, value: filters.length.toString() });
        }
      };

      // Add all filter counts
      addFilterCount("country", selectedCountries);
      addFilterCount("league", selectedLeagues);
      addFilterCount("rarity", selectedVersions);
      addFilterCount("clubs", selectedClubs);
      addFilterCount("pastAndPresentClub", selectedPnPs);
      addFilterCount("metal", selectedMetals);

      return activeFilters;
    },
  );

export const {
  updateFormation,
  updateBudget,
  updateExcludeIconsHeroes,
  updateSelectedLiveEventId,
  updateMinRating,
  updateMaxRating,
  updateScope,
  updateSelectedCountries,
  updateSelectedLeagues,
  updateSelectedClubs,
  updateSelectedPnPs,
  updateSelectedMetals,
  updateChangingPitchItemOnIndex,
  addPlayerOrChangeRole,
  switchRightPanelMode,
  removePlayer,
  resetRole,
  updateSelectedVersions,
  clearSquad,
  internalMovePlayer,
  sortBuiltSquads,
  addSquad,
  removeTempSquad,
  exitPickSquad,
  updateManager,
  selectChemStyle,
  // updatePlayerMetaRatings,
  clearPlayerFilters,
  updateCustomFilter,
  clearCustomFilter,
  switchLeftPanelMode,
  setChosenTactic,
  clearFilter,
  clearAllFilters,
  updateLoadedSquadIndex,
  updateTacticInsertStatus,
  clearRecommendedPlayers,
  updateIncludeLoanPlayers,
  loadPlayStyleFilterOptions,
  updateBestFormation,
  addBestFormationIncludedPlayer,
  removeBestFormationIncludedPlayer,
  updateHasChangedTactic,
  updateStatusesToIdle,
  setSaveSquadStatusToIdle,
  updatePlayersMetaRatingsForAllRoles,
} = defaultSquadBuilderSlice.actions;

export default defaultSquadBuilderSlice.reducer;
